import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { driverCheapestSelection } from '../lib/offerEligibility';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';

function normalizeGtin(value) {
  const digits = String(value || '').replace(/\D/g, '');
  if (![8, 12, 13, 14].includes(digits.length)) return null;
  let sum = 0;
  for (let i = digits.length - 2; i >= 0; i -= 1) {
    const fromRight = digits.length - 1 - i;
    sum += Number(digits[i]) * (fromRight % 2 === 1 ? 3 : 1);
  }
  return ((10 - (sum % 10)) % 10) === Number(digits.at(-1)) ? digits : null;
}

function requestId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, character => {
    const value = Math.floor(Math.random() * 16);
    return (character === 'x' ? value : (value & 3) | 8).toString(16);
  });
}

function supplierList(names) {
  const values = Array.isArray(names) && names.length ? names : ['Booker', 'Bestway', 'Parfetts'];
  return values.length < 2 ? (values[0] || 'suppliers') : `${values.slice(0, -1).join(', ')} and ${values.at(-1)}`;
}

function SupplierCard({ result, comparable, cheapest }) {
  const names = { Booker: '#B9772A', Bestway: '#1B4DE4', Parfetts: '#D71920' };
  const color = names[result?.supplier] || '#5B6B85';
  if (!result) return null;
  const statusCopy = {
    notFound: 'No exact barcode match',
    loginRequired: 'Supplier login required',
    timeout: 'Supplier timed out',
    error: result.message || 'Supplier search failed',
  };
  return (
    <section style={{ background:'#fff', border:'1px solid #E3E8F1', borderRadius:16, padding:16, boxShadow:'0 6px 20px rgba(13,27,42,.05)' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
        <strong style={{ fontFamily:'Poppins,sans-serif', color, fontSize:16 }}>{result.supplier}</strong>
        {cheapest && comparable ? <span style={{ background:'#EAF7EF', color:'#0F9E57', borderRadius:20, padding:'4px 9px', fontSize:10, fontWeight:700 }}>LOWEST</span> : null}
      </div>
      {result.status === 'ok' && result.product ? (
        <div style={{ marginTop:12 }}>
          <div style={{ color:'#0D1B2A', fontWeight:700, lineHeight:1.35 }}>{result.product.name}</div>
          <div style={{ color:'#8A97AD', fontSize:12, marginTop:5 }}>
            {result.product.sku ? `#${result.product.sku}` : 'SKU unavailable'} · {result.product.packSize || 'Pack not stated'}
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'end', marginTop:16 }}>
            <div><div style={{ color:'#8A97AD', fontSize:10, textTransform:'uppercase', fontWeight:700 }}>Case ex VAT</div><div style={{ color:'#0D1B2A', fontSize:27, fontWeight:800 }}>£{Number(result.product.casePriceExVat).toFixed(2)}</div></div>
            <div style={{ textAlign:'right', color:result.product.stockStatus === 'outOfStock' ? '#D6453F' : '#0F9E57', fontSize:11, fontWeight:700 }}>{String(result.product.stockStatus).replace(/([A-Z])/g, ' $1')}</div>
          </div>
          {result.product.casePriceIncVat != null ? <div style={{ color:'#8A97AD', fontSize:11, marginTop:3 }}>£{Number(result.product.casePriceIncVat).toFixed(2)} including VAT</div> : null}
        </div>
      ) : <div style={{ marginTop:18, color:result.status === 'loginRequired' ? '#C2871F' : '#6B7894', fontSize:13 }}>{statusCopy[result.status] || result.message}</div>}
    </section>
  );
}

export default function LiveSearch() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scanTimer = useRef(null);
  const hubRef = useRef(null);
  const pendingJobRef = useRef(null);
  const shopIdRef = useRef('');
  const basketRequestRef = useRef(null);
  const [barcode, setBarcode] = useState('');
  const [comparison, setComparison] = useState(null);
  const [capabilities, setCapabilities] = useState(null);
  const [capabilityStatus, setCapabilityStatus] = useState('checking');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [scanning, setScanning] = useState(false);
  const [watchInterval, setWatchInterval] = useState('1h');
  const [watchMessage, setWatchMessage] = useState('');
  const [shops, setShops] = useState([]);
  const [shopId, setShopId] = useState('');
  const [shopName, setShopName] = useState('');
  const [agentOnline, setAgentOnline] = useState(null);
  const [jobStatus, setJobStatus] = useState('');
  const [pairing, setPairing] = useState(null);
  const [basketQuantity, setBasketQuantity] = useState(1);
  const [basketBusy, setBasketBusy] = useState(false);
  const [basketResult, setBasketResult] = useState(null);

  useEffect(() => {
    let disposed = false;
    const controller = new AbortController();
    const capabilityTimeout = window.setTimeout(() => controller.abort(), 8000);
    async function initialize() {
      try {
        const response = await fetch('/api/capabilities', { cache:'no-store', signal:controller.signal });
        if (!response.ok) throw new Error(`Driver capabilities unavailable (HTTP ${response.status}).`);
        const nextCapabilities = await response.json();
        if (disposed) return;
        setCapabilities(nextCapabilities);
        setCapabilityStatus('ready');
        if (nextCapabilities?.executionLocation !== 'hosted') return;

        const meResponse = await fetch('/api/account/me');
        if (meResponse.status === 401) { navigate('/login?returnUrl=/live-search', { replace:true }); return; }
        if (!meResponse.ok) throw new Error('Could not load your ShopkeeperIQ account.');
        const me = await meResponse.json();
        if (disposed) return;
        setShops(me.shops || []);
        const initialShopId = me.shops?.[0]?.shopId || '';
        setShopId(initialShopId);
        shopIdRef.current = initialShopId;

        const hub = new HubConnectionBuilder()
          .withUrl('/hubs/client')
          .withAutomaticReconnect()
          .configureLogging(LogLevel.Warning)
          .build();
        hub.on('jobUpdated', applyJobUpdate);
        hub.on('agentStatus', status => {
          if (String(status.shopId) === String(shopIdRef.current)) setAgentOnline(Boolean(status.online));
        });
        await hub.start();
        if (disposed) { await hub.stop(); return; }
        hubRef.current = hub;
        if (initialShopId) await refreshAgentStatus(initialShopId);
      } catch (e) {
        if (!disposed) {
          setCapabilityStatus('unavailable');
          setError(e.name === 'AbortError'
            ? 'The driver did not answer /api/capabilities within 8 seconds.'
            : (e.message || 'Could not initialize the driver.'));
        }
      } finally {
        window.clearTimeout(capabilityTimeout);
      }
    }
    initialize();
    return () => {
      disposed = true;
      controller.abort();
      window.clearTimeout(capabilityTimeout);
      stopCamera();
      const hub = hubRef.current;
      hubRef.current = null;
      if (hub) hub.stop().catch(() => {});
    };
  }, [navigate]);

  function applyJobUpdate(job) {
    if (!job || !pendingJobRef.current || String(job.id) !== String(pendingJobRef.current)) return;
    const labels = { dispatched:'Sent to shop driver…', running:`Searching ${supplierList(capabilities?.suppliers)}…`, completed:'Comparison ready', failed:'Search failed', expired:'Search expired' };
    setJobStatus(labels[job.status] || job.status);
    if (job.status === 'completed' && job.result) { setComparison(job.result); setLoading(false); pendingJobRef.current = null; }
    if (job.status === 'failed' || job.status === 'expired') { setError(job.message || 'The remote search failed.'); setLoading(false); pendingJobRef.current = null; }
  }

  async function refreshAgentStatus(id = shopId) {
    if (!id) { setAgentOnline(null); return; }
    const response = await fetch(`/api/agents/status?shopId=${encodeURIComponent(id)}`);
    if (response.status === 401) { navigate('/login', { replace:true }); return; }
    if (!response.ok) return;
    const agents = await response.json();
    setAgentOnline(agents.some(agent => agent.online));
  }

  async function pollJob(id) {
    for (let attempt = 0; attempt < 100 && pendingJobRef.current === id; attempt += 1) {
      await new Promise(resolve => setTimeout(resolve, 900));
      const response = await fetch(`/api/jobs/${encodeURIComponent(id)}?poll=${attempt}`, { cache:'no-store' });
      if (response.status === 401) { navigate('/login', { replace:true }); return; }
      if (!response.ok) continue;
      const job = await response.json();
      applyJobUpdate(job);
      if (['completed','failed','expired'].includes(job.status)) return;
    }
    if (pendingJobRef.current === id) { pendingJobRef.current = null; setLoading(false); setError('The hosted search did not complete in time.'); }
  }

  async function search(value = barcode) {
    const normalized = normalizeGtin(value);
    if (!normalized) { setError('Enter a valid 8, 12, 13 or 14 digit barcode.'); return; }
    stopCamera(); basketRequestRef.current = null; setBarcode(normalized); setLoading(true); setError(''); setComparison(null); setWatchMessage(''); setBasketResult(null);
    try {
      if (capabilities?.executionLocation === 'hosted') {
        if (!shopId) throw new Error('Create or select a shop before searching.');
        const response = await fetch('/api/search', {
          method:'POST',
          headers:{ 'Content-Type':'application/json', 'X-ShopkeeperIQ-Client':'1' },
          body:JSON.stringify({ shopId, barcode:normalized }),
        });
        const body = await response.json();
        if (response.status === 401) { navigate('/login', { replace:true }); return; }
        if (!response.ok) throw new Error(body.detail || body.title || 'Remote price search failed.');
        pendingJobRef.current = body.id;
        applyJobUpdate(body);
        await pollJob(body.id);
        return;
      }
      const response = await fetch(`/api/products/search?barcode=${encodeURIComponent(normalized)}`);
      const body = await response.json();
      if (!response.ok) throw new Error(body.detail || 'Price search failed.');
      setComparison(body);
    } catch (e) { setError(e.message || 'Could not reach the supplier driver.'); }
    finally { setLoading(false); }
  }

  async function createShop() {
    if (!shopName.trim()) { setError('Enter a shop name.'); return; }
    const response = await fetch('/api/shops', { method:'POST', headers:{ 'Content-Type':'application/json', 'X-ShopkeeperIQ-Client':'1' }, body:JSON.stringify({ name:shopName }) });
    const body = await response.json();
    if (!response.ok) { setError(body.detail || body.title || 'Could not create the shop.'); return; }
    const next = { shopId:body.id, name:body.name, role:body.role };
    setShops([next]); setShopId(body.id); shopIdRef.current = body.id; setShopName(''); setAgentOnline(false); setError('');
  }

  async function createPairingCode() {
    const response = await fetch(`/api/shops/${encodeURIComponent(shopId)}/agents/pairing`, {
      method:'POST', headers:{ 'Content-Type':'application/json', 'X-ShopkeeperIQ-Client':'1' }, body:JSON.stringify({ name:'Shop Windows PC' }),
    });
    const body = await response.json();
    if (!response.ok) { setError(body.detail || body.title || 'Could not create a pairing code.'); return; }
    setPairing(body); setError('');
  }

  async function startCamera() {
    setError('');
    if (!navigator.mediaDevices?.getUserMedia) { setError('Camera scanning is unavailable in this browser. Enter the barcode manually.'); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video:{ facingMode:{ ideal:'environment' } }, audio:false });
      streamRef.current = stream; setScanning(true);
      setTimeout(async () => {
        if (!videoRef.current) return;
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        if (!('BarcodeDetector' in window)) { setError('Automatic barcode detection is unavailable here. You can still read and type the number below.'); return; }
        const detector = new window.BarcodeDetector({ formats:['ean_8','ean_13','upc_a','upc_e','itf'] });
        scanTimer.current = window.setInterval(async () => {
          try {
            const codes = await detector.detect(videoRef.current);
            const value = codes[0]?.rawValue;
            if (value && normalizeGtin(value)) search(value);
          } catch { /* keep the camera open while frames settle */ }
        }, 500);
      }, 0);
    } catch { setError('Camera permission was denied. Enter the barcode manually.'); }
  }

  function stopCamera() {
    if (scanTimer.current) window.clearInterval(scanTimer.current);
    scanTimer.current = null;
    streamRef.current?.getTracks().forEach(track => track.stop());
    streamRef.current = null;
    setScanning(false);
  }

  async function saveWatch() {
    try {
      const response = await fetch(`/api/watchlist/${barcode}`, { method:'PUT', headers:{ 'Content-Type':'application/json' }, body:JSON.stringify({ interval:watchInterval }) });
      const body = await response.json();
      if (!response.ok) throw new Error(body.detail || 'Could not save monitoring.');
      setWatchMessage(`Watching every ${body.interval}.`);
    } catch (e) { setWatchMessage(e.message); }
  }

  async function addCheapest() {
    if (!recommended?.product || basketBusy || basketResult) return;
    const quantity = Math.max(1, Math.min(99, Number(basketQuantity) || 1));
    const prompt = `Add ${quantity} case${quantity === 1 ? '' : 's'} of ${recommended.product.name} to the ${recommended.supplier} basket for £${Number(recommended.product.casePriceExVat).toFixed(2)} per case?`;
    if (capabilities?.basketConfirmationRequired && !window.confirm(prompt)) return;
    const currentRequestId = basketRequestRef.current || requestId();
    basketRequestRef.current = currentRequestId;
    setBasketBusy(true); setBasketResult(null); setError('');
    try {
      if (capabilities?.executionLocation === 'hosted') {
        if (!shopId) throw new Error('Create or select a shop before adding to a supplier basket.');
        const response = await fetch('/api/basket/add-cheapest', {
          method:'POST',
          headers:{ 'Content-Type':'application/json', 'X-ShopkeeperIQ-Client':'1' },
          body:JSON.stringify({ shopId, barcode, quantity, confirmed:true, requestId:currentRequestId }),
        });
        const body = await response.json();
        if (response.status === 401) { navigate('/login', { replace:true }); return; }
        if (!response.ok) throw new Error(body.detail || body.title || 'Could not send the basket action to the shop driver.');
        for (let attempt = 0; attempt < 190; attempt += 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          const jobResponse = await fetch(`/api/jobs/${encodeURIComponent(body.id)}?poll=${attempt}`, { cache:'no-store' });
          if (jobResponse.status === 401) { navigate('/login', { replace:true }); return; }
          if (!jobResponse.ok) continue;
          const job = await jobResponse.json();
          if (job.status === 'completed') {
            if (!job.result) throw new Error('The shop driver returned no basket result. Check the supplier basket before retrying.');
            setBasketResult(job.result);
            return;
          }
          if (job.status === 'failed' || job.status === 'expired')
            throw new Error(job.message || 'The basket action did not complete. Check the supplier basket before retrying.');
        }
        throw new Error('The basket action is still pending. Check the supplier basket before retrying.');
      }
      const response = await fetch('/api/basket/add-cheapest', {
        method:'POST',
        headers:{ 'Content-Type':'application/json', 'X-ShopkeeperIQ-Client':'1' },
        body:JSON.stringify({ barcode, quantity, confirmed:true, requestId:currentRequestId }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.message || body.detail || body.title || 'Could not add the product to the supplier basket.');
      setBasketResult(body);
    } catch (e) { setError(e.message || 'Could not change the supplier basket.'); }
    finally { setBasketBusy(false); }
  }

  const ok = comparison?.suppliers?.filter(x => x.status === 'ok' && x.product) || [];
  const eligible = ok.filter(result => result.product.stockStatus !== 'outOfStock');
  const comparable = comparison?.cheapest?.packsComparable === true;
  const recommended = driverCheapestSelection(comparison).result || null;
  const lowest = recommended?.supplier || null;
  const supplierNames = supplierList(capabilities?.suppliers);

  return (
    <div style={{ minHeight:'100dvh', background:'#F4F6FB', overflowY:'auto', fontFamily:'Inter,sans-serif' }}>
      <header style={{ background:'#0D1B2A', color:'#fff', padding:'14px 16px 24px' }}>
        <button onClick={() => navigate(-1)} style={{ border:0, background:'rgba(255,255,255,.1)', color:'#fff', borderRadius:10, width:38, height:38, fontSize:20, cursor:'pointer' }} aria-label="Back">←</button>
        <h1 style={{ fontFamily:'Poppins,sans-serif', fontSize:23, margin:'18px 0 4px' }}>Live price check</h1>
        <p style={{ color:'#AEC0DC', fontSize:13, margin:0 }}>Scan once. Check {supplierNames}.</p>
        <div data-testid="driver-build" style={{ color:'#7F96B8', fontSize:10, marginTop:7, fontFamily:'ui-monospace,SFMono-Regular,Consolas,monospace' }}>
          Driver build: {capabilities?.buildVersion || (capabilityStatus === 'checking' ? 'checking…' : 'unavailable')}{capabilities?.browserMode ? ` · Chromium ${capabilities.browserMode}` : ''}
        </div>
      </header>
      <main style={{ padding:16, display:'grid', gap:14 }}>
        {capabilities?.executionLocation === 'hosted' ? <section style={{ background:'#fff', borderRadius:16, padding:14, border:'1px solid #E3E8F1' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:10 }}><strong style={{ color:'#0D1B2A' }}>Shop connection</strong>{shopId ? <span style={{ color:agentOnline?'#0F9E57':'#C2871F', fontSize:11, fontWeight:700 }}>{agentOnline?'DRIVER ONLINE':'DRIVER OFFLINE'}</span> : null}</div>
          {shops.length ? <select value={shopId} onChange={e => { setShopId(e.target.value); shopIdRef.current = e.target.value; setPairing(null); refreshAgentStatus(e.target.value); }} style={{ width:'100%', marginTop:10, border:'1px solid #D6DEEA', borderRadius:10, padding:10 }}>{shops.map(shop => <option key={shop.shopId} value={shop.shopId}>{shop.name}</option>)}</select> : <div style={{ display:'flex', gap:8, marginTop:10 }}><input value={shopName} onChange={e => setShopName(e.target.value)} placeholder="Shop name" style={{ minWidth:0, flex:1, border:'1px solid #D6DEEA', borderRadius:10, padding:10 }} /><button onClick={createShop} style={{ border:0, borderRadius:10, background:'#1B4DE4', color:'#fff', padding:'0 14px', fontWeight:700 }}>Create</button></div>}
          {shopId && !agentOnline ? <button onClick={createPairingCode} style={{ width:'100%', marginTop:10, border:'1px solid #1B4DE4', borderRadius:10, background:'#EEF2FE', color:'#1B4DE4', padding:10, fontWeight:700 }}>Pair the shop computer</button> : null}
          {pairing ? <div style={{ marginTop:10, background:'#F4F6FB', borderRadius:10, padding:10, color:'#5B6B85', fontSize:11, lineHeight:1.5 }}><div>Run this in PowerShell within 10 minutes:</div><code style={{ display:'block', marginTop:6, overflowWrap:'anywhere', color:'#0D1B2A', userSelect:'all' }}>&amp; "$env:LOCALAPPDATA\Programs\ShopkeeperIQ Driver\current\ShopkeeperIQ.Driver.exe" pair {window.location.origin} {pairing.pairingCode}</code></div> : null}
          {jobStatus ? <div style={{ marginTop:9, color:'#5B6B85', fontSize:11 }}>{jobStatus}</div> : null}
        </section> : null}
        <section style={{ background:'#fff', borderRadius:16, padding:14, border:'1px solid #E3E8F1' }}>
          {scanning ? <video ref={videoRef} muted playsInline style={{ width:'100%', aspectRatio:'4/3', objectFit:'cover', borderRadius:12, background:'#101923', marginBottom:12 }} /> : null}
          <div style={{ display:'flex', gap:8 }}>
            <input value={barcode} onChange={e => setBarcode(e.target.value.replace(/\D/g, '').slice(0,14))} onKeyDown={e => e.key === 'Enter' && search()} inputMode="numeric" placeholder="Scan or enter barcode" aria-label="Barcode" style={{ minWidth:0, flex:1, border:'1.5px solid #D6DEEA', borderRadius:11, padding:'12px', fontSize:15, outline:'none' }} />
            <button onClick={scanning ? stopCamera : startCamera} style={{ border:0, borderRadius:11, padding:'0 13px', background:'#EEF2FE', color:'#1B4DE4', fontWeight:700, cursor:'pointer' }}>{scanning ? 'Stop' : 'Scan'}</button>
          </div>
          <button disabled={loading} onClick={() => search()} style={{ width:'100%', border:0, borderRadius:12, padding:13, marginTop:10, background:loading?'#9AAEEA':'#1B4DE4', color:'#fff', fontWeight:700, fontSize:14, cursor:loading?'default':'pointer' }}>{loading ? `Checking ${supplierNames}…` : 'Compare live prices'}</button>
          {error ? <div role="alert" style={{ color:'#D6453F', fontSize:12, marginTop:10, lineHeight:1.4 }}>{error}</div> : null}
        </section>
        {comparison ? <div style={{ color:'#8A97AD', fontSize:11 }}>Fetched via {comparison.executionLocation} driver · {new Date(comparison.completedAt).toLocaleTimeString()}</div> : null}
        {comparison?.suppliers?.map(result => <SupplierCard key={result.supplier} result={result} comparable={comparable} cheapest={result.supplier === lowest} />)}
        {eligible.length > 1 && !comparable ? <div style={{ background:'#FFF8E8', color:'#7A5410', border:'1px solid #F0DBB6', borderRadius:12, padding:12, fontSize:12 }}>Pack sizes differ or are unavailable, so case prices are not ranked.</div> : null}
        {eligible.length > 1 && comparable && !recommended ? <div style={{ background:'#FFF8E8', color:'#7A5410', border:'1px solid #F0DBB6', borderRadius:12, padding:12, fontSize:12 }}>Multiple suppliers have the same lowest case price. Choose a supplier manually.</div> : null}
        {comparison && capabilities?.basketAutomation && recommended ? <section style={{ background:'#fff', borderRadius:16, padding:14, border:'1px solid #E3E8F1' }}>
          <strong style={{ color:'#0D1B2A' }}>Add the cheapest option</strong>
          <div style={{ color:'#5B6B85', fontSize:12, marginTop:5 }}>{recommended.supplier} · £{Number(recommended.product.casePriceExVat).toFixed(2)} per case</div>
          <div style={{ display:'flex', gap:8, marginTop:11 }}>
            <input type="number" min="1" max="99" value={basketQuantity} onChange={event => setBasketQuantity(event.target.value)} aria-label="Case quantity" style={{ width:72, border:'1px solid #D6DEEA', borderRadius:10, padding:10, fontSize:14 }} />
            <button disabled={basketBusy || Boolean(basketResult)} onClick={addCheapest} style={{ flex:1, border:0, borderRadius:10, background:basketBusy?'#9AAEEA':'#16B364', color:'#fff', padding:10, fontWeight:700, cursor:basketBusy?'default':'pointer' }}>{basketBusy?'Adding to supplier basket…':basketResult?'Basket action completed':`Add to ${recommended.supplier} basket`}</button>
          </div>
          {basketResult ? <div role="status" style={{ marginTop:10, color:basketResult.status === 'added'?'#0F9E57':'#9A6717', fontSize:12, lineHeight:1.45 }}>{basketResult.message}{basketResult.basketUrl ? <> <a href={basketResult.basketUrl} target="_blank" rel="noreferrer" style={{ color:'#1B4DE4', fontWeight:700 }}>Open basket</a></> : null}</div> : null}
        </section> : null}
        {comparison && capabilities?.monitoring ? <section style={{ background:'#fff', borderRadius:16, padding:14, border:'1px solid #E3E8F1' }}><strong style={{ color:'#0D1B2A' }}>Monitor this barcode</strong><div style={{ display:'flex', gap:8, marginTop:10 }}><select value={watchInterval} onChange={e => setWatchInterval(e.target.value)} style={{ flex:1, border:'1px solid #D6DEEA', borderRadius:10, padding:10 }}><option value="15m">Every 15 minutes</option><option value="1h">Hourly</option><option value="6h">Every 6 hours</option><option value="24h">Daily</option></select><button onClick={saveWatch} style={{ border:0, borderRadius:10, background:'#0D1B2A', color:'#fff', padding:'0 15px', fontWeight:700 }}>Watch</button></div>{watchMessage ? <div style={{ marginTop:8, color:'#5B6B85', fontSize:11 }}>{watchMessage}</div> : null}</section> : null}
      </main>
    </div>
  );
}
